import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Zap, Globe, Server, Cpu, TrendingUp } from 'lucide-react';

const LiveBar = ({ label, base }) => {
  const [val, setVal] = useState(base);
  useEffect(() => {
    const iv = setInterval(() => {
      setVal(Math.max(20, Math.min(98, base + (Math.random() * 20 - 10))));
    }, 1400);
    return () => clearInterval(iv);
  }, [base]);
  return (
    <div>
      <div className="flex justify-between text-[11px] font-mono text-slate-500 mb-1">
        <span>{label}</span>
        <span className="text-sky-600 font-bold">{val.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-sky-400 via-blue-400 to-purple-500" animate={{ width: `${val}%` }} transition={{ duration: 1.2, ease: 'easeInOut' }} />
      </div>
    </div>
  );
};

const UptimeDashboard = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 1500);
    return () => clearInterval(iv);
  }, []);

  const regions = [
    { name: 'Mumbai', latency: 12 + (tick % 4), status: 'operational' },
    { name: 'Bangalore', latency: 18 + (tick % 3), status: 'operational' },
    { name: 'Delhi', latency: 22 + (tick % 5), status: 'operational' },
    { name: 'Singapore', latency: 45 + (tick % 6), status: 'operational' },
    { name: 'Frankfurt', latency: 128 + (tick % 8), status: 'operational' },
    { name: 'Virginia', latency: 210 + (tick % 10), status: 'operational' }
  ];

  return (
    <section className="section-padding section-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-sky-50/50 to-white pointer-events-none" />
      <div className="container-x relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            All systems operational
          </div>
          <h2 className="section-title-dark">Live Infrastructure <span className="gradient-text">Status</span></h2>
          <p className="section-subtitle-dark mx-auto mt-4">Real-time metrics from our global datacenter fleet &mdash; the numbers that keep your business online.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-6 rounded-2xl glass-light-card shadow-xl relative overflow-hidden">
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sky-600">
                  <Activity size={18} /><span className="text-sm font-semibold">Live Metrics</span>
                </div>
                <span className="text-[10px] font-mono text-slate-500">Updated now</span>
              </div>
              <div className="space-y-4">
                <LiveBar label="CPU Load" base={42} />
                <LiveBar label="Memory" base={58} />
                <LiveBar label="Network I/O" base={35} />
                <LiveBar label="Storage IOPS" base={72} />
                <LiveBar label="Power Efficiency" base={88} />
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="p-6 rounded-2xl glass-light-card shadow-lg">
            <div className="flex items-center gap-2 text-sky-600 mb-4">
              <Globe size={18} /><span className="text-sm font-semibold">Global Latency</span>
            </div>
            <div className="space-y-2.5">
              {regions.map((r) => (
                <div key={r.name} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-sky-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-sm font-medium text-slate-800">{r.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-slate-700">{r.latency}ms</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 font-semibold uppercase">OK</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="grid grid-cols-2 gap-4">
            {[
              { Icon: Server, label: 'Active Servers', val: '4,820', color: 'from-cyan-500 to-sky-600' },
              { Icon: Zap, label: 'Requests/sec', val: '1.2M', color: 'from-purple-500 to-fuchsia-600' },
              { Icon: TrendingUp, label: 'Uptime (30d)', val: '99.99%', color: 'from-emerald-500 to-teal-600' },
              { Icon: Cpu, label: 'Compute Hrs', val: '284K', color: 'from-orange-500 to-amber-600' }
            ].map((k) => (
              <div key={k.label} className="p-5 rounded-2xl glass-light-card group">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${k.color} flex items-center justify-center mb-3 shadow-md group-hover:scale-110 transition-transform`}>
                  <k.Icon className="text-white" size={18} />
                </div>
                <div className="text-2xl font-extrabold text-slate-900">{k.val}</div>
                <div className="text-xs text-slate-500 mt-0.5">{k.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default UptimeDashboard;
